"use client"

import type { ReactNode } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { homeKeys as k } from "@/i18n/home-keys"
import Image from "next/image"
import Link from "next/link"
import { useLocalePath } from "@/lib/use-localized-navigation"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import { blobMediaUrl } from "@/lib/blob-url"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { useCountry } from "@/components/layout/country-provider"

/**
 * Shared "Experts behind your health" grid — count + university logos on the
 * left, doctor photo grid with hover-reveal bio pill on the right.
 *
 * The homepage passes an optional `heading` intro ("The experts behind your
 * health journey"); /go landing pages render it without one. Returns null
 * when the experts list is empty so hosts can place it unconditionally.
 */
interface DoctorsSectionProps {
  initialExperts?: ReviewerExpert[]
  /** Optional intro rendered above the count+grid. */
  heading?: ReactNode
  /**
   * Optional heading rendered at the TOP of the left column, above the
   * count. The homepage uses this to sit "The experts behind your health
   * journey" directly above the 30,000+ stat, in the same column container.
   */
  columnHeading?: ReactNode
  /** Override grid max-width (default 1440px). */
  maxWidthClass?: string
  /** Override the section's vertical padding. Defaults to homepage spacing. */
  paddingClass?: string
  /** Override the desktop grid column template (default 1fr / 2fr split). */
  gridColsClassName?: string
  /**
   * When true, append a white "Read more about us" card as the final tile of
   * the photo grid (same aspect/rounding as the doctor tiles) and drop the
   * left-column outline button so the CTA isn't duplicated. The reviews page
   * opts in so the slot left by trimming the expert list reads as an
   * intentional CTA rather than a gap.
   */
  aboutCtaCard?: boolean
  /**
   * Show the `doctorsCount` stat (e.g. "30,000+ MRI's reviewed over 10+ years").
   * Defaults to true. Set false on surfaces where the MRI-framed count is
   * off-topic (e.g. the blood-test immunity LP).
   */
  showCount?: boolean
  /**
   * Override the stat label text. Defaults to the `doctorsCountLabel`
   * translation. The all-English /teams page passes an English literal so the
   * label doesn't render localized (e.g. German) amid its hardcoded copy.
   */
  countLabel?: ReactNode
}

export function DoctorsSection({
  initialExperts,
  heading,
  columnHeading,
  maxWidthClass = "max-w-[1440px]",
  paddingClass = "py-16 md:py-24",
  gridColsClassName = "md:grid-cols-[1fr_2fr]",
  aboutCtaCard = false,
  showCount = true,
  countLabel,
}: DoctorsSectionProps) {
  const t = useTranslations("home")
  const lp = useLocalePath()
  const experts = initialExperts ?? []
  // AT: the experts module is off on every Austrian page, not per host.
  // country-gate-ignore: AT content hide, not a page gate
  const { code: country } = useCountry()

  if (country === "at") return null

  if (experts.length === 0) return null

  return (
    <section className={`lazy-section ${paddingClass}`}>
      {heading}
      <div
        className={`mx-auto grid ${maxWidthClass} grid-cols-1 gap-8 px-4 ${gridColsClassName} md:gap-12 md:px-10`}
      >
        {/* Left column: count, university logos, link to About.
            Flex column + `mt-auto` on the link pins the CTA to the
            bottom of the column on desktop (where the row inherits its
            height from the photo grid on the right). On mobile the
            link still flows under the logos naturally. */}
        <div className="flex flex-col">
          {columnHeading ? <div className="mb-6">{columnHeading}</div> : null}
          <div>
            {showCount ? (
              <>
                <p className="type-display-md text-neutral-900">
                  {t(k.doctorsCount)}
                </p>
                <p className="type-body-lg mt-2 text-neutral-600">
                  {countLabel ?? t(k.doctorsCountLabel)}
                </p>
              </>
            ) : null}
            <div className="mt-6 flex items-center gap-5">
              <Image
                src={blobMediaUrl("/images/logos/oxford.svg")}
                alt="University of Oxford"
                width={100}
                height={25}
                className="h-6 w-auto opacity-50"
              />
              <Image
                src={blobMediaUrl("/images/logos/karolinska.svg")}
                alt="Karolinska Institutet"
                width={66}
                height={27}
                className="h-6 w-auto opacity-50"
              />
              <Image
                src={blobMediaUrl("/images/logos/st-gallen.svg")}
                alt="University of St.Gallen"
                width={109}
                height={22}
                className="h-5 w-auto opacity-50"
              />
            </div>
          </div>
          {/* DS outline button — `mt-auto` pushes it to the bottom
              of the left column so it aligns with the bottom edge of
              the photo grid on desktop. Hidden when `aboutCtaCard` is on,
              since the trailing grid tile carries the same CTA. */}
          {aboutCtaCard ? null : (
            <Link href={lp("/about")} className="mt-6 self-start md:mt-auto">
              <Button variant="outline">{t(k.doctorsLink)}</Button>
            </Link>
          )}
        </div>

        {/* Right column: doctors grid */}
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3">
          {experts.map((doctor) => (
            <div key={doctor.name}>
              <div className="group relative aspect-[326/397] overflow-hidden rounded-[12px]">
                <Image
                  src={doctor.image}
                  alt={doctor.name}
                  fill
                  sizes="(max-width: 768px) 50vw, 33vw"
                  className="object-cover"
                />
                {/* Dark overlay — fades in on hover to darken the photo */}
                <div className="absolute inset-0 bg-black opacity-0 transition-opacity duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:opacity-40" />
                {/* Pills — name always visible; bio expands on hover */}
                <div className="absolute right-2 bottom-2 left-2 z-10 flex flex-col gap-1">
                  <div className="rounded-[8px] bg-white/[0.77] px-3 py-2.5 backdrop-blur-sm">
                    <p className="type-title-md leading-snug text-neutral-900">
                      {doctor.name}
                    </p>
                  </div>
                  <div className="max-h-0 overflow-hidden opacity-0 transition-[max-height,opacity] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:max-h-40 group-hover:opacity-100">
                    <div className="rounded-[8px] bg-white/[0.24] px-3 py-2.5">
                      <p className="type-body-md leading-snug text-white">
                        {doctor.title}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Trailing CTA tile — white card matching the doctor tiles,
              links through to the About page. Content pinned bottom-left to
              echo the doctor name pills. Opt-in via `aboutCtaCard`. */}
          {aboutCtaCard ? (
            <Link
              href={lp("/about")}
              className="group flex aspect-[326/397] flex-col items-start justify-end gap-3 rounded-[12px] border border-white bg-white/80 p-5 transition-colors hover:bg-white"
            >
              <span className="type-title-md translate-y-[5px] leading-snug text-neutral-900">
                {t(k.doctorsLink)}
              </span>
              <ArrowRightIcon className="h-7 w-7 translate-y-[2px] text-neutral-900 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-1" />
            </Link>
          ) : null}
        </div>
      </div>
    </section>
  )
}
